import { useEffect, useSyncExternalStore } from 'react';

/** What the person can see of the page while an on-screen keyboard may be open. */
export type VisualViewportMetrics = {
  /** Height of the visible part of the page, in px. */
  height: number;
  /** How far the visible part sits below the top of the layout viewport (iOS pans it for the keyboard). */
  offsetTop: number;
  /** How much of the bottom of the layout viewport the on-screen keyboard covers, in px. */
  keyboardInset: number;
};

/** The CSS custom properties the app root publishes, read by the classes in `components/keyboardAwareClasses`. */
export const VISUAL_VIEWPORT_CSS_VARIABLES = {
  keyboardInset: '--keyboard-inset',
  height: '--visual-viewport-height',
  offsetTop: '--visual-viewport-offset-top',
} as const;

/** Same touch-only test as `useFieldLayout`: a phone or tablet, which is what has an on-screen keyboard. */
const ON_SCREEN_KEYBOARD_QUERY = '(pointer: coarse) and (hover: none)';

export const hasOnScreenKeyboard = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(ON_SCREEN_KEYBOARD_QUERY).matches;

/**
 * The visible part of the page, or null where there's no on-screen keyboard to make room for:
 * no `visualViewport` (jsdom, old browsers), a mouse-driven screen, or a pinch-zoomed page (where
 * the visible part is small because of the zoom, not a keyboard).
 */
export const readVisualViewport = (): VisualViewportMetrics | null => {
  if (typeof window === 'undefined') return null;
  const viewport = window.visualViewport;
  if (!viewport || !hasOnScreenKeyboard()) return null;
  if (Math.abs((viewport.scale ?? 1) - 1) > 0.01) return null;
  return {
    height: Math.round(viewport.height),
    offsetTop: Math.round(viewport.offsetTop),
    keyboardInset: Math.max(
      0,
      Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
    ),
  };
};

/** Calls `onChange` whenever the visible part of the page moves or resizes (the keyboard opens or closes). */
export const subscribeToVisualViewport = (onChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  const viewport = window.visualViewport;
  viewport?.addEventListener('resize', onChange);
  viewport?.addEventListener('scroll', onChange);
  window.addEventListener('resize', onChange);
  return () => {
    viewport?.removeEventListener('resize', onChange);
    viewport?.removeEventListener('scroll', onChange);
    window.removeEventListener('resize', onChange);
  };
};

const isSameMetrics = (a: VisualViewportMetrics | null, b: VisualViewportMetrics | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.height === b.height &&
    a.offsetTop === b.offsetTop &&
    a.keyboardInset === b.keyboardInset);

let lastSnapshot: VisualViewportMetrics | null = null;
const getSnapshot = () => {
  const next = readVisualViewport();
  if (!isSameMetrics(lastSnapshot, next)) lastSnapshot = next;
  return lastSnapshot;
};
const getServerSnapshot = () => null;

/**
 * The visible part of the page and how much of it the on-screen keyboard covers, kept current.
 * Null where there's no on-screen keyboard (desktop, jsdom). Most components don't need this:
 * the classes in `components/keyboardAwareClasses` read the same values from CSS variables.
 */
export const useVisualViewport = (): VisualViewportMetrics | null =>
  useSyncExternalStore(subscribeToVisualViewport, getSnapshot, getServerSnapshot);

/**
 * Writes the metrics to CSS custom properties on `root`, or removes them when there's nothing to
 * track, so CSS falls back to what it used before (no keyboard inset, the full screen height).
 */
export const publishVisualViewport = (
  metrics: VisualViewportMetrics | null,
  root: HTMLElement = document.documentElement,
) => {
  if (!metrics) {
    Object.values(VISUAL_VIEWPORT_CSS_VARIABLES).forEach((name) => root.style.removeProperty(name));
    return;
  }
  root.style.setProperty(VISUAL_VIEWPORT_CSS_VARIABLES.keyboardInset, `${metrics.keyboardInset}px`);
  root.style.setProperty(VISUAL_VIEWPORT_CSS_VARIABLES.height, `${metrics.height}px`);
  root.style.setProperty(VISUAL_VIEWPORT_CSS_VARIABLES.offsetTop, `${metrics.offsetTop}px`);
};

/**
 * Publishes the visible part of the page as CSS variables on the document root, for as long as
 * the calling component is mounted. Mount it once, at the app root (`KeyboardAwareLayout`).
 * It writes straight to the root's style, so nothing re-renders as the keyboard moves.
 */
export const usePublishVisualViewport = () => {
  useEffect(() => {
    const update = () => publishVisualViewport(readVisualViewport());
    update();
    const unsubscribe = subscribeToVisualViewport(update);
    return () => {
      unsubscribe();
      publishVisualViewport(null);
    };
  }, []);
};
